"use client";

import {useEffect, useMemo, useRef} from "react";
import {useNextStep} from "nextstepjs";
import {useAuthContext} from "@/contexts/common/AuthContextProvider";
import {useUserAcknowledgement} from "@/hooks/useUserAcknowledgement";
import {DASHBOARD_TOUR_ACKNOWLEDGEMENT} from "@/utils/acknowledgements/userAcknowledgements";

const TOUR_VERSION = "v1";
export const DASHBOARD_TOUR_NAME = "dashboardTour";

interface DashboardTourProps {
   onboardingCompleted: boolean;
}

function buildStorageKey(orgId: string | undefined, userId: string | undefined): string | null {
   if (!orgId || !userId) return null;
   return `gail_dashboard_tour_seen_${TOUR_VERSION}_${orgId}_${userId}`;
}

export default function DashboardTour({onboardingCompleted}: Readonly<DashboardTourProps>) {
   const {user} = useAuthContext();
   const {startNextStep} = useNextStep();
   const startedRef = useRef(false);

   const storageKey = useMemo(
      () => buildStorageKey(user?.orgId ?? undefined, user?.id ?? undefined),
      [user?.orgId, user?.id]
   );
   const dashboardTourAcknowledgement = useUserAcknowledgement({
      descriptor: DASHBOARD_TOUR_ACKNOWLEDGEMENT,
      orgId: user?.orgId,
      userId: user?.id,
      legacyStorageKeys: storageKey ? [storageKey] : [],
   });

   useEffect(() => {
      if (!storageKey || !onboardingCompleted || startedRef.current) return;
      if (globalThis.window === undefined) return;
      if (!dashboardTourAcknowledgement.isResolved || dashboardTourAcknowledgement.isAcknowledged) return;

      const alreadySeen = localStorage.getItem(storageKey) === "true";
      if (!alreadySeen) {
         const timer = setTimeout(() => {
            startedRef.current = true;
            startNextStep(DASHBOARD_TOUR_NAME);
         }, 800);
         return () => clearTimeout(timer);
      }
   }, [storageKey, onboardingCompleted, startNextStep, dashboardTourAcknowledgement.isResolved, dashboardTourAcknowledgement.isAcknowledged]);

   return null;
}

export function useDashboardTourCallbacks() {
   const {user} = useAuthContext();

   const storageKey = useMemo(
      () => buildStorageKey(user?.orgId ?? undefined, user?.id ?? undefined),
      [user?.orgId, user?.id]
   );
   const dashboardTourAcknowledgement = useUserAcknowledgement({
      descriptor: DASHBOARD_TOUR_ACKNOWLEDGEMENT,
      orgId: user?.orgId,
      userId: user?.id,
      legacyStorageKeys: storageKey ? [storageKey] : [],
   });

   return useMemo(
      () => ({
         onComplete: () => {
            if (!storageKey || globalThis.window === undefined) return;
            localStorage.setItem(storageKey, "true");
            void dashboardTourAcknowledgement.acknowledge("completed", {
               source: "dashboard_tour",
            });
         },
         onSkip: () => {
            if (!storageKey || globalThis.window === undefined) return;
            localStorage.setItem(storageKey, "true");
            void dashboardTourAcknowledgement.acknowledge("skipped", {
               source: "dashboard_tour",
            });
         },
      }),
      [storageKey, dashboardTourAcknowledgement]
   );
}
